// List-sort helpers: the API takes a single `sort=<field>,<asc|desc>` param
// (docs/ai-strategy/api-list-sort.md); the filter sheet decomposes it into a field group and
// a direction group so new fields are a one-entry addition here.

export type SortField = 'created' | 'updated' | 'status';
export type SortDir = 'asc' | 'desc';

// 'name' was removed when the user-facing strategy name was dropped (2026-07-13). The column
// is gone from the backend too (delta 021), so there is nothing to sort by.
export const SORT_FIELDS: SortField[] = ['created', 'updated', 'status'];

export const parseSort = (sort: string | undefined): { field: SortField; dir: SortDir } => {
    const [field, dir] = (sort ?? '').split(',');
    return {
        field: (SORT_FIELDS as string[]).includes(field) ? (field as SortField) : 'created',
        dir: dir === 'asc' ? 'asc' : 'desc',
    };
};

// undefined = the server default (created,desc) — the list then sends no sort param at all.
export const composeSort = (field: SortField, dir: SortDir): string | undefined =>
    field === 'created' && dir === 'desc' ? undefined : `${field},${dir}`;

export const sortFieldLabelKey = (field: SortField): string => `ai-strategy-sort-${field}`;

// Direction labels read naturally for the field type: dates offer newest/oldest, text fields
// (status) offer A→Z / Z→A. Status compares the raw status code, not the translated chip
// label — the value is grouping equal statuses, not the exact order.
export const sortDirLabelKey = (field: SortField, dir: SortDir): string => {
    if (field === 'created' || field === 'updated') {
        return dir === 'desc' ? 'ai-strategy-sort-newest' : 'ai-strategy-sort-oldest';
    }
    return dir === 'asc' ? 'ai-strategy-sort-az' : 'ai-strategy-sort-za';
};
